/**
 * Küchenwerkzeuge in der Seitenansicht. Jedes Werkzeug wird im eigenen
 * Koordinatensystem gezeichnet: Der Arbeitspunkt (Besenspitze, Löffelmulde,
 * Schaberblatt) liegt im Ursprung, der Griff zeigt nach oben (negatives y).
 * Platziert und gedreht wird es mit place().
 */
import { STROKE } from '../palette.ts';
import type { Tone } from '../palette.ts';
import { ellipsePath, path, roundedRectPath } from '../paths.ts';
import { circle, group, line, rect, shape, styles } from '../scene.ts';
import type { MaybeNode, SceneNode } from '../scene.ts';

/** Platziert ein Werkzeug: Arbeitspunkt nach (x, y), Drehung um den Arbeitspunkt. */
export function place(
  x: number,
  y: number,
  rotate: number,
  children: readonly MaybeNode[],
  scale = 1,
): SceneNode {
  return group(children, { transform: { x, y, rotate, scale, ox: 0, oy: 0 } });
}

/**
 * Schneebesen. Die Drähte werden mit der Drehung um die eigene Achse
 * gestaucht, so dass sich der Besen sichtbar dreht.
 * @param spin Drehung um die Längsachse, 0 bis 1 = eine halbe Umdrehung.
 */
export function whisk(spin = 0): SceneNode[] {
  const back: SceneNode[] = [];
  const front: SceneNode[] = [];
  const count = 4;
  for (let index = 0; index < count; index += 1) {
    const angle = (index / count + spin) * Math.PI;
    const spread = 15 * Math.cos(angle);
    const tone: Tone = Math.sin(angle) >= 0 ? 'ink' : 'deep';
    const wire = shape(
      path()
        .move(-2.5, -34)
        .curve(-2.5 + spread * 0.5, -26, spread, -12, spread * 0.55, -3)
        .quad(0, 3, -spread * 0.55, -3)
        .curve(-spread, -12, 2.5 - spread * 0.5, -26, 2.5, -34)
        .toString(),
      { stroke: tone, strokeWidth: STROKE.detail },
    );
    (tone === 'ink' ? front : back).push(wire);
  }
  return [
    ...back,
    ...front,
    shape(roundedRectPath(-4, -80, 8, 46, 4), styles.solid('paper')),
    rect(-5, -38, 10, 6, 2, styles.solid('deep')),
    circle(0, -74, 1.6, styles.flat('ink')),
  ];
}

/** Kochlöffel aus Holz. */
export function woodenSpoon(): SceneNode[] {
  return [
    shape(roundedRectPath(-2.8, -86, 5.6, 76, 2.8), styles.solid('tint')),
    shape(ellipsePath(0, -2, 8, 11), styles.solid('tint')),
    shape(ellipsePath(0, -2, 4.5, 7), { fill: 'shade', stroke: 'none' }),
    line(0, -80, 0, -20, styles.fine('shade')),
  ];
}

/** Teigschaber mit biegsamem Blatt. */
export function spatula(bend = 0): SceneNode[] {
  const tip = bend * 6;
  return [
    shape(roundedRectPath(-3, -84, 6, 60, 3), styles.solid('ink')),
    shape(
      path()
        .move(-12, -26)
        .line(12, -26)
        .curve(12, -14, 12 + tip, -6, 8 + tip, 0)
        .quad(tip, 3, -8 + tip, 0)
        .curve(-12 + tip, -6, -12, -14, -12, -26)
        .close()
        .toString(),
      styles.solid('paper'),
    ),
  ];
}

/** Pfannenwender. Das Blatt liegt waagerecht am Ursprung, der Stiel geht nach links oben. */
export function turner(): SceneNode[] {
  return [
    // Hals vom Blatt schräg nach oben zum Griff
    shape(path().move(-14, -2).line(-30, -26).toString(), { stroke: 'ink', strokeWidth: STROKE.outline }),
    group([shape(roundedRectPath(-4, -40, 8, 40, 4), styles.solid('ink'))], {
      transform: { x: -30, y: -26, rotate: -34, ox: 0, oy: 0 },
    }),
    shape(roundedRectPath(-20, -4, 40, 6, 2.5), styles.solid('paper')),
    line(-12, -1, 14, -1, styles.fine('deep')),
  ];
}

/** Holzstäbchen für die Garprobe; Spitze im Ursprung. */
export function skewer(): SceneNode[] {
  return [
    shape(path().move(-1.8, -74).line(1.8, -74).line(0.9, -2).line(0, 0).line(-0.9, -2).close().toString(), {
      fill: 'tint',
      stroke: 'ink',
      strokeWidth: STROKE.detail,
    }),
  ];
}

/** Nudelholz von vorn, liegt waagerecht, Mitte im Ursprung. */
export function rollingPin(roll = 0): SceneNode[] {
  const barrel = roundedRectPath(-42, -9, 84, 18, 7);
  const marks: SceneNode[] = [];
  for (let index = 0; index < 5; index += 1) {
    const position = (((index / 5 + roll) % 1) + 1) % 1;
    const x = -42 + position * 84;
    marks.push(line(x, -9, x, 9, styles.fine('shade')));
  }
  return [
    shape(roundedRectPath(-64, -4, 128, 8, 4), styles.solid('ink')),
    shape(barrel, styles.flat('tint')),
    group(marks, { clip: barrel }),
    line(-38, -4, 38, -4, { stroke: 'paper', strokeWidth: STROKE.detail }),
    shape(barrel, styles.outline()),
  ];
}

/** Backpinsel; die Borsten zeigen nach unten. */
export function brush(): SceneNode[] {
  return [
    shape(roundedRectPath(-3, -64, 6, 42, 3), styles.solid('tint')),
    rect(-6.5, -25, 13, 8, 1.5, styles.solid('deep')),
    shape(
      path().move(-6.5, -17).line(6.5, -17).line(5, 0).quad(0, 3, -5, 0).close().toString(),
      styles.solid('paper'),
    ),
    line(-2.5, -14, -2, -2, styles.fine('shade')),
    line(2.5, -14, 2, -2, styles.fine('shade')),
  ];
}

/** Gabel; die Zinken zeigen nach unten, Spitze im Ursprung. */
export function fork(): SceneNode[] {
  const tines: SceneNode[] = [-4.5, -1.5, 1.5, 4.5].map((x) =>
    line(x, -16, x * 0.9, 0, { stroke: 'ink', strokeWidth: STROKE.detail }),
  );
  return [
    shape(roundedRectPath(-3, -78, 6, 52, 3), styles.solid('paper')),
    shape(
      path().move(-3, -28).line(3, -28).line(6, -16).line(-6, -16).close().toString(),
      styles.solid('paper'),
    ),
    ...tines,
  ];
}

/**
 * Handrührgerät von der Seite mit zwei Rührbesen. Die Besenspitzen liegen im
 * Ursprung, das Gerät steht darüber.
 * @param spin Drehung der Besen, 0 bis 1 = eine halbe Umdrehung.
 */
export function handMixer(spin = 0): SceneNode[] {
  const beater = (offset: number): SceneNode[] => {
    const wires: SceneNode[] = [];
    for (let index = 0; index < 2; index += 1) {
      const angle = (index / 2 + spin) * Math.PI;
      const spread = 7 * Math.cos(angle);
      wires.push(
        shape(
          path()
            .move(offset - 1.5, -26)
            .curve(offset + spread, -20, offset + spread * 1.1, -6, offset + spread * 0.5, -1)
            .quad(offset, 2, offset - spread * 0.5, -1)
            .curve(offset - spread * 1.1, -6, offset - spread, -20, offset + 1.5, -26)
            .toString(),
          { stroke: Math.sin(angle) >= 0 ? 'ink' : 'deep', strokeWidth: STROKE.detail },
        ),
      );
    }
    return [line(offset, -50, offset, -26, { stroke: 'ink', strokeWidth: STROKE.detail }), ...wires];
  };
  const body = path()
    .move(-22, -50)
    .line(30, -50)
    .quad(40, -50, 40, -60)
    .line(40, -66)
    .quad(40, -76, 28, -76)
    .line(-16, -76)
    .quad(-28, -76, -28, -64)
    .line(-28, -58)
    .quad(-28, -50, -22, -50)
    .close()
    .toString();
  const handle = path()
    .move(-14, -76)
    .quad(-12, -96, 6, -96)
    .line(22, -96)
    .quad(34, -96, 32, -76)
    .line(24, -76)
    .quad(24, -88, 16, -88)
    .line(4, -88)
    .quad(-4, -88, -4, -76)
    .close()
    .toString();
  return [
    ...beater(-6),
    ...beater(6),
    shape(handle, styles.solid('paper')),
    shape(body, styles.solid('paper')),
    rect(-20, -58, 6, 4, 1.5, styles.flat('deep')),
    line(-28, -62, 40, -62, styles.fine('shade')),
  ];
}

/**
 * Stabmixer: Motorteil mit Schalter, Schaft und Glocke. Die Unterkante der
 * Glocke liegt im Ursprung.
 * @param running Leuchtet der Schalter, läuft das Gerät.
 */
export function stickBlender(running = false): SceneNode[] {
  return [
    shape(roundedRectPath(-3, -66, 6, 56, 3), styles.solid('paper')),
    shape(
      path().move(-12, 0).line(-9, -12).line(9, -12).line(12, 0).quad(0, -4, -12, 0).close().toString(),
      styles.solid('paper'),
    ),
    shape(roundedRectPath(-10, -112, 20, 50, 8), styles.solid('ink')),
    shape(roundedRectPath(-4, -102, 8, 12, 3), styles.solid(running ? 'accent' : 'deep')),
    line(-7, -70, 7, -70, styles.fine('deep')),
  ];
}
